import { PriceStatus } from '@toggle/helpers';
import { Entity, mapEntity, PriceHistory } from '@toggle/toggle';
import clsx from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { NEW_YORK_TIMEZONE } from '~/hooks/use-price-market-reaction-data/usePriceMarketReactionData';

import { formatProportionChange } from '../utils';
import styles from './CatalystSingleMarketReactionPrice.module.scss';
import { formatPrice, priceStatusClassMap } from './utils';

export interface CatalystSingleMarketReactionPriceProps {
  entity: Entity;
  isWindowComplete: boolean;
  isAfterHoursChart?: boolean;
  eventProportionChange?: number;
  prevCloseData?: PriceHistory[number];
  lastKnownPrice?: number;
  lastKnownDate?: string;
}

export const CatalystSingleMarketReactionPrice = ({
  entity,
  isWindowComplete,
  isAfterHoursChart,
  eventProportionChange,
  prevCloseData,
  lastKnownPrice,
  lastKnownDate,
}: CatalystSingleMarketReactionPriceProps) => {
  const { t } = useTranslation(['insights']);

  if (eventProportionChange === undefined) {
    return null;
  }

  const mappedEntity = mapEntity(entity);
  const formattedPrice = lastKnownPrice
    ? formatPrice(lastKnownPrice, mappedEntity)
    : undefined;

  const getDateLabel = () => {
    const referenceDate = lastKnownDate ?? prevCloseData?.time;

    if (!referenceDate) {
      return null;
    }

    if (isWindowComplete && !isAfterHoursChart) {
      return (
        <span className={clsx(styles.dateSpan)}>
          {t('insights:catalyst.drawer.asOfDateClose', {
            date: formatInTimeZone(new Date(referenceDate), 'UTC', 'MMM d'),
          })}
        </span>
      );
    }

    return (
      <span className={clsx(styles.dateSpan)}>
        {t('insights:catalyst.drawer.asOfDate', {
          date: `${formatInTimeZone(
            new Date(referenceDate),
            NEW_YORK_TIMEZONE,
            'MMM d, h:mm a'
          )} ET`,
        })}
      </span>
    );
  };

  return (
    <div
      data-testid="catalyst-market-reaction-price"
      className={styles.wrapper}
    >
      {getDateLabel()}
      <div className={clsx(styles.proportionChangeContainer)}>
        {formattedPrice !== undefined && (
          <>
            <div className={styles.priceRow}>
              <span className={styles.proportionPrice}>{formattedPrice}</span>
              <span className={styles.proportionCurrency}>
                {mappedEntity.currency}
              </span>
            </div>
            <span
              className={clsx(
                styles.percentageChange,
                priceStatusClassMap[
                  eventProportionChange > 0
                    ? PriceStatus.Positive
                    : eventProportionChange < 0
                    ? PriceStatus.Negative
                    : PriceStatus.Default
                ]
              )}
            >
              {formatProportionChange(eventProportionChange)}
            </span>
          </>
        )}
      </div>
    </div>
  );
};
